import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | paid | pending | error
  const [error, setError] = useState("");

  const verify = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session_id");
      return;
    }
    let tries = 0;
    while (tries < 5) {
      tries += 1;
      try {
        const r = await api.get(`/checkout/verify/${sessionId}`);
        if (r.data.status === "paid") {
          setStatus("paid");
          // Refresh user
          try { await api.get("/auth/me"); } catch {}
          // Phase 6: route to upsell interstitial if active offers exist
          try {
            const ups = await api.get("/upsells/active");
            if ((ups.data.upsells || []).length > 0) {
              navigate("/checkout/upsell", { replace: true });
              return;
            }
          } catch (e) { /* swallow — fall through to success */ }
          return;
        }
      } catch (e) {
        console.error("verify failed", e);
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    setStatus("pending");
  }, [sessionId, navigate]);

  useEffect(() => { verify(); }, [verify]);

  return (
    <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="max-w-lg w-full bg-[#060A24] border border-white/10 p-10 text-center">
        {status === "verifying" && (
          <>
            <div className="w-12 h-12 mx-auto mb-6 border-2 border-fc-red border-t-transparent rounded-full animate-spin" />
            <h1 className="fc-h-section text-2xl mb-2" data-testid="checkout-status-title">Activating your membership…</h1>
            <p className="text-white/65 text-sm">We're confirming your payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#10B981] mb-4" size={48} />
            <h1 className="fc-h-section text-3xl mb-3" data-testid="checkout-success-title">You're in.</h1>
            <p className="text-white/70 mb-8">Your membership is active. Time to build.</p>
            <button onClick={() => navigate("/dashboard")} className="btn-fc-red w-full justify-center" data-testid="checkout-go-to-dashboard">
              <Sparkles size={16} /> Go to Dashboard
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <AlertCircle className="mx-auto text-[#F59E0B] mb-4" size={48} />
            <h1 className="fc-h-section text-2xl mb-3">Almost there…</h1>
            <p className="text-white/70 mb-6 text-sm">
              Your payment hasn't confirmed yet. Refresh the page in a few moments, or check your dashboard — we'll activate automatically once it clears.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-fc-outline w-full justify-center">Go to Dashboard</button>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="mx-auto text-fc-red mb-4" size={48} />
            <h1 className="fc-h-section text-2xl mb-3">Something went wrong</h1>
            <p className="text-white/65 text-sm mb-6">{error}</p>
            <button onClick={() => navigate("/pricing")} className="btn-fc-red w-full justify-center">Back to Pricing</button>
          </>
        )}
      </div>
    </div>
  );
}
